/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import { FaUserInjured } from "react-icons/fa";

import { useApi } from "@/hooks/useApi";
import { useSwal } from "@/hooks/useSwal";
import { usePagination } from "@/hooks/usePagination";
import { useDebounce } from "@/../_metronic/helpers";
import { transformData } from "@/utils/transformData";
import { paginatedSurgicalAttendanceMap } from "@/utils/mappings/surgicalAttendance";

import { SearchFormControl } from "@/components/FormControl/SearchFormControl";
import { SkeletonWrapper } from "@/components/SkeletonWrapper";
import { Avatar } from "@/components/Avatar";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";

type PatientListProps = {
	search: string;
	setSearch: (search: string) => void;
	onPatientAdmit: (surgicalAttendance: SurgicalAttendance) => void;
	height: string;
	showSearch?: boolean;
	searchPlaceholder?: string;
	className?: string;
	isPatientAdmitted?: boolean;
};

export function PatientList({
	search,
	setSearch,
	onPatientAdmit,
	height,
	showSearch = true,
	searchPlaceholder = "Pesquisar paciente",
	className = "",
	isPatientAdmitted = false,
}: PatientListProps) {
	const [surgicalAttendances, setSurgicalAttendances] = useState<PaginatedSurgicalAttendances>(
		{} as PaginatedSurgicalAttendances
	);

	const { page, forcePage, isFirstRender, handleNextPage, resetPagination } = usePagination();
	const debouncedSearch = useDebounce(search, 750);

	const { api } = useApi();
	const { Toast } = useSwal();

	const [isLoading, setIsLoading] = useState(true);

	const SKELETON_COUNT = 5;

	const results = surgicalAttendances.results;
	const dataLength = results?.length || 1;
	const hasResults = Boolean(results?.length);
	const shouldShowSkeletons = !hasResults && isLoading;
	const shouldShowNoResults = !hasResults && !isLoading;

	async function getSurgicalAttendances(isSearch?: boolean, pageToUse?: number) {
		setIsLoading(true);

		try {
			const { data } = await api.get("surgical-center/surgical-attendances", {
				params: { page: pageToUse || (isSearch ? 1 : page), search },
			});

			const transformedData = transformData<PaginatedSurgicalAttendances>(
				data,
				paginatedSurgicalAttendanceMap
			);

			if (isSearch) {
				setSurgicalAttendances(transformedData);
			} else {
				const combinedResults = [...(results || []), ...transformedData.results];
				setSurgicalAttendances({
					...transformedData,
					results: combinedResults,
				});
			}
		} catch {
			Toast.fire({
				title: "Erro ao buscar pacientes!",
				icon: "error",
			});
		} finally {
			setIsLoading(false);
		}
	}

	function handleNextPageWrapper(pageToUse: number) {
		getSurgicalAttendances(false, pageToUse);
	}

	function refreshPatientList() {
		resetPagination();
		setSurgicalAttendances({} as PaginatedSurgicalAttendances);
		getSurgicalAttendances(true);
	}

	function renderSkeletonCard(index: number) {
		return (
			<div
				key={`skeleton-card-${index}`}
				className="border-gray-300 border-dotted rounded mx-3 my-4 p-5 d-flex gap-2"
			>
				<div className="rounded-circle bullet-30">
					<SkeletonWrapper circle height="100%" />
				</div>
				<SkeletonWrapper count={2} containerClassName="flex-grow-1" />
			</div>
		);
	}

	function renderSkeletonAvatar(index: number) {
		return <Avatar skeleton key={`skeleton-avatar-${index}`} />;
	}

	function renderSkeletonLoader() {
		const skeletonArray = Array.from({ length: SKELETON_COUNT });

		if (showSearch) {
			return skeletonArray.map((_, index) => renderSkeletonCard(index));
		}

		return skeletonArray.map((_, index) => renderSkeletonAvatar(index));
	}

	function renderPatientCard(surgicalAttendance: SurgicalAttendance, index: number) {
		const patientName = surgicalAttendance.patient.name;
		const expertiseName = surgicalAttendance.expertise.name;

		return (
			<div
				key={`patient-card-${index}`}
				className="d-flex align-items-center justify-content-between border-gray-300 border-dotted rounded mx-3 my-4 p-5"
			>
				<div className="d-flex">
					<div className="d-flex flex-column justify-content-center pe-5">
						<Avatar
							patientId={surgicalAttendance.patientId}
							patientName={patientName}
						/>
					</div>

					<div className="d-flex flex-column">
						<Card.Title className="fs-5 m-0">{patientName}</Card.Title>
						<div className="text-gray-700">{expertiseName}</div>
					</div>
				</div>

				<Button
					className="hover-elevate-up ms-2 py-1"
					onClick={() => onPatientAdmit(surgicalAttendance)}
				>
					Admitir
				</Button>
			</div>
		);
	}

	function renderPatientAvatar(surgicalAttendance: SurgicalAttendance, index: number) {
		return (
			<div key={`patient-avatar-${index}`} className="d-flex justify-content-center py-6">
				<Avatar
					patientId={surgicalAttendance.patientId}
					patientName={surgicalAttendance.patient.name}
				/>
			</div>
		);
	}

	function renderPatientList() {
		if (!hasResults) {
			return null;
		}

		if (showSearch) {
			return results.map((surgicalAttendance, index) =>
				renderPatientCard(surgicalAttendance, index)
			);
		}

		return results.map((surgicalAttendance, index) =>
			renderPatientAvatar(surgicalAttendance, index)
		);
	}

	function renderContent() {
		const content = renderPatientList();

		if (content) {
			return content;
		}

		if (shouldShowSkeletons) {
			return renderSkeletonLoader();
		}

		if (shouldShowNoResults) {
			return (
				<NoRecordsFeedback
					icon={<FaUserInjured />}
					message="Nenhum resultado encontrado"
				/>
			);
		}

		return null;
	}

	useEffect(() => {
		forcePage.current = page;
		getSurgicalAttendances();
	}, [page]);

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			return;
		}

		refreshPatientList();
	}, [debouncedSearch]);

	useEffect(() => {
		if (isPatientAdmitted) {
			refreshPatientList();
		}
	}, [isPatientAdmitted]);

	return (
		<div className={className}>
			{showSearch && (
				<div className="mt-5 px-3 mb-5">
					<SearchFormControl
						placeholder={searchPlaceholder}
						value={search}
						onChange={(event) => setSearch(event.target.value)}
					/>
				</div>
			)}

			<InfiniteScroll
				height={height}
				dataLength={dataLength}
				next={() => handleNextPage(handleNextPageWrapper)}
				hasMore={hasResults && results?.length !== surgicalAttendances.meta?.total}
				loader={renderSkeletonLoader()}
			>
				{renderContent()}
			</InfiniteScroll>
		</div>
	);
}
